import { useEffect, useRef, useState } from 'react';

import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';

import L from 'leaflet';

import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet/dist/leaflet.css';
import 'leaflet-geosearch/dist/geosearch.css';
import './stylesComponent.scss';
import LoaderSpin from './LoaderSpin';
import { useTranslation } from 'react-i18next';

const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

const SALocation = {
  lat: 24.7136,
  lng: 46.6753,
};

type setLocationType = ({
  lat,
  lng,
  countryCode,
  currentAddress,
}: {
  lat: number;
  lng: number;
  countryCode?: string;
  currentAddress?: string;
}) => void;

type mapType = {
  location?: {
    lat: number | undefined;
    lng: number | undefined;
    currentAddress?: string;
  };
  updateLocation?: setLocationType;
  viewOnly?: boolean;
  withSearch?: boolean;
  height?: string;
  width?: string;
  searchLabel?: string;
};

export default function Map({
  viewOnly = false,
  updateLocation,
  location,
  withSearch,
  height = '400px',
  width = '400px',
  dataTestId = 'map-container',
  searchLabel,
}: mapType & { dataTestId?: string }) {
  const popupRef = useRef(null);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (location?.lat && location?.lng) {
      // Wait for marker to mount and ref to be set
      const timeout = setTimeout(() => {
        if (popupRef.current) {
          //@ts-expect-error
          popupRef.current.openPopup();
        }
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, [location]);

  if (!mounted)
    return (
      <div className="flex justify-center items-center h-full min-h-[200px] w-full">
        <LoaderSpin />
      </div>
    );

  return (
    <div data-testid={dataTestId} style={{ height, width, position: 'relative' }}>
      <MapContainer
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        center={[
          location?.lat || SALocation.lat,
          location?.lng || SALocation.lng,
        ]}
        {...(viewOnly
          ? {
              dragging: false,
              zoomControl: false,
              scrollWheelZoom: false,
              touchZoom: false,
              doubleClickZoom: false,
              boxZoom: false,
              attributionControl: false,
            }
          : {})}
      >
        {/* Base minimal map */}
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png" />

        {/* Labels layer */}
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png" />

        {withSearch && (
          <SearchControl
            selectLocation={updateLocation}
            currentAddress={location?.currentAddress}
            searchLabel={searchLabel}
            disabled={viewOnly}
          />
        )}

        {!viewOnly && <MapOverlayClick selectLocation={updateLocation} />}

        {!!location?.lat && !!location?.lng && (
          <Marker position={[location?.lat, location?.lng]} ref={popupRef}>
            <Popup>{location?.currentAddress}</Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}

// Input search for map
const SearchControl = ({
  selectLocation,
  currentAddress,
  searchLabel,
  disabled = false,
}: {
  selectLocation?: setLocationType;
  currentAddress?: string;
  searchLabel?: string;
  disabled?: boolean;
}) => {
  const map = useMap();
  const { t } = useTranslation();

  useEffect(() => {
    const provider = new OpenStreetMapProvider();

    //@ts-expect-error
    const searchControl = new GeoSearchControl({
      provider,
      style: 'bar',
      autoComplete: true,
      retainZoomLevel: false,
      searchLabel: searchLabel || t('components.enter_address', { defaultValue: 'Enter address' }),
      keepResult: true,
      showMarker: false,
    });

    map.addControl(searchControl);

    // Initial sync of input value if label exists
    if (currentAddress) {
      // Use a small timeout to ensure the control is added to the DOM
      setTimeout(() => {
        const searchInput = document.querySelector(
          '.leaflet-control-geosearch form input'
        );
        if (searchInput) {
          (searchInput as HTMLInputElement).value = currentAddress;
        }
        if (disabled) {
          const resetBtn = document.querySelector(
            '.leaflet-control-geosearch .reset'
          );
          if (resetBtn) resetBtn.remove();

          searchInput?.setAttribute('disabled', 'true');
        }
      }, 100);
    }

    map.on(
      'geosearch/showlocation',
      async (e: unknown) => {
        const event = e as {
          location: {
            x: number;
            y: number;
            label: string;
          };
        };
        const { x, y, label } = event.location;

        try {
          // Initial update with label from search
          selectLocation?.({ lat: y, lng: x, currentAddress: label });

          // Fetch country code details
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${y}&lon=${x}&format=json`
          );

          if (!response.ok) throw new Error('Geocoding failed');

          const data = await response.json();

          selectLocation?.({
            lat: y,
            lng: x,
            currentAddress: label,
            countryCode: data?.address?.country_code,
          });
        } catch (error) {
          console.error('GeoSearch error:', error);
        }
      }
    );

    return () => {
      map.removeControl(searchControl);
    };
  }, [map, selectLocation, currentAddress, searchLabel, t, disabled]);

  return null;
};

// Map overlay for click event
const MapOverlayClick = ({
  selectLocation,
}: {
  selectLocation?: setLocationType;
}) => {
  useMapEvents({
    click: async (e: {
      latlng: {
        lng: number;
        lat: number;
      };
    }) => {
      const { lat, lng } = e.latlng;

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
        );

        if (!response.ok) throw new Error('Reverse geocoding failed');

        const data = await response.json();

        selectLocation?.({
          lat,
          lng,
          currentAddress: data?.display_name,
          countryCode: data?.address?.country_code,
        });
      } catch (error) {
        console.error('Map click error:', error);
        // Fallback: still update coordinates even if address lookup fails
        selectLocation?.({
          lat,
          lng,
          currentAddress: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        });
      }
    },
  });

  return null;
};
